import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { Box, Stack } from '@mui/material';
import Chip from '@/components/chip';
import { FC, useMemo } from 'react';
import Text from '@/components/text';
import RiskChip from '@/components/chip/risk-chip';
import AccordionButton from '@/components/accordion-button';
import { getEventsDurationLabel, getEventsDurationMinutes } from '@/components/timeline/helpers';
import { DateFormat, getNiceDate, getUserTimezone, pluralize } from '@/lib/helpers';
import TimelineItem from '@/components/timeline/timeline-item';
import ConversationItem from './conversation-item';
import { getDomainLabels, groupBucketByConversation, pickHighestRiskLevel } from './helpers';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { getUserEventPublicService } from '@/lib/services/public-service.service';
import { UserEventAction } from '@/lib/models/user-event';
import ActionChip from '@/components/action-chip';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';

interface Props {
  session: UserEventModel[];
  isLastInGroup: boolean;
}

const SessionItem: FC<Props> = ({ session, isLastInGroup }) => {
  const firstEvent = session[0];
  const { policyDecision, fileUpload, serviceName: firstServiceName } = firstEvent;
  const { data: publicServices = [] } = useAppData<PublicServiceMeta[]>(
    AppDataQueryKey.PublicServices
  );
  const publicService = getUserEventPublicService(firstEvent, publicServices);
  const { name: serviceName = firstServiceName, image = '' } = publicService || {};

  const hasViolations = session.some((event) => !!event.violations.length);
  const conversations = useMemo(() => groupBucketByConversation(session), [session]);
  const conversationCount = conversations.length;

  const highestRiskLevel = pickHighestRiskLevel(session.map(({ riskLevel }) => riskLevel));

  const domainLabels = getDomainLabels(session);
  const userEventAction = policyDecision.decision;
  const showActionChip = ![UserEventAction.ALLOW, UserEventAction.REROUTE].includes(
    userEventAction
  );

  // Only show 'violation chip' for 'intercept'/'redact'
  const hideViolationsForActions = [
    UserEventAction.INTERCEPTAUTH,
    UserEventAction.PASSTHROUGH,
    UserEventAction.BLOCK,
    UserEventAction.INLINEBLOCK,
  ].includes(userEventAction);
  const showViolationsChip = hasViolations && !hideViolationsForActions;

  const leftNode = useMemo(() => {
    const first = session[0];
    const last = session[session.length - 1];
    const { timezone } = getUserTimezone();
    const startFormatted = getNiceDate(first.startTime, DateFormat.TIME_12H, timezone).formatted;
    const endFormatted = getNiceDate(last.endTime, DateFormat.TIME_12H, timezone).formatted;
    const durationMinutes = getEventsDurationMinutes(session, false);
    const showRange = startFormatted !== endFormatted && durationMinutes > 2;
    const duration = getEventsDurationLabel(session);

    return (
      <Stack gap={0.25} alignItems="flex-end">
        <Text size="small">{startFormatted}</Text>
        {showRange && <Text size="small">{endFormatted}</Text>}
        <Text size="x-small" color="grey">
          {duration}
        </Text>
      </Stack>
    );
  }, [session]);

  return (
    <TimelineItem
      leftNode={leftNode}
      timelineImage={image}
      timelineImageAlt={serviceName}
      isLastItem={isLastInGroup}
    >
      <Box width="100%">
        <AccordionButton
          title={
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              width="100%"
            >
              <Box minWidth={0}>
                <Text size="small" bold>
                  {serviceName}
                </Text>
                <Text size="x-small" color="grey">
                  {`${conversationCount} ${pluralize(conversationCount, 'conversation')}`}
                  {domainLabels && ` • ${domainLabels}`}
                </Text>
              </Box>
              <Stack direction="row" gap={1} flexShrink={0} alignItems="center">
                {highestRiskLevel && (
                  <RiskChip value={highestRiskLevel} size="x-small" showRiskLabel />
                )}
                {showViolationsChip && <Chip size="x-small" color="error" label="Violations" />}
                {showActionChip && (
                  <ActionChip action={policyDecision} fileUpload={fileUpload} size="x-small" />
                )}
              </Stack>
            </Stack>
          }
        >
          <Stack gap={1}>
            {conversations.map((conversation) => (
              <ConversationItem key={conversation[0].id} events={conversation} />
            ))}
          </Stack>
        </AccordionButton>
      </Box>
    </TimelineItem>
  );
};

export default SessionItem;
